// appClock.js - ONE app-wide clock + display time zone.
// appNow(): server time. useChartData registers its ack/pong-synced clock
// (window.__cheddarNow); until then we fall back to the local clock plus the
// last learned server offset. Never trust the PC wall clock alone.
// Display TZ: purely for labels (footer clock, chart time axis). Candle
// bucketing stays on epoch ms (UTC), so the zone can never move a candle.
// Offset sources, best first: ws (ack/pong round-trip), http (Date header
// of our own origin, ~1s), trade (trade timestamps). A weaker source only
// applies when no stronger one has reported in the last 2 minutes.
// appNow() = Date.now() + offset: the PC's wall clock (and its UTC, DST or
// zone being wrong) never decides the time shown or the slot a candle opens.
const RANK = { trade: 1, http: 2, ws: 3 }
const _clk = { offset: 0, source: 'none', rank: 0, at: 0 }
export function setServerOffset(ms, source = 'trade') {
  if (!Number.isFinite(ms)) return
  const r = RANK[source] || 1, now = performance.now()
  if (r < _clk.rank && now - _clk.at < 120000) return
  _clk.offset = ms; _clk.source = source; _clk.rank = r; _clk.at = now
}
export const appNow = () => Date.now() + _clk.offset
export const clockInfo = () => ({ source: _clk.source, offsetMs: Math.round(_clk.offset), pc: new Date().toISOString(), server: new Date(appNow()).toISOString() })
async function syncHttp() {
  try {
    const t0 = performance.now()
    const r = await fetch('/?clk=' + Date.now(), { method: 'HEAD', cache: 'no-store' })
    const d = Date.parse(r.headers.get('date') || '')
    if (!Number.isFinite(d)) return
    const rtt = performance.now() - t0
    // Date header is whole seconds: +500ms centres it, +rtt/2 for transit.
    setServerOffset(d + 500 + rtt / 2 - Date.now(), 'http')
  } catch { /* offline: keep last */ }
}
if (typeof window !== 'undefined') {
  syncHttp(); setInterval(syncHttp, 10 * 60 * 1000)
  window.__cheddarClock = clockInfo
}

const TZ_KEY = 'cheddar_display_tz'
export const TZ_OPTIONS = [
  { id: 'local', label: 'Local (this PC)' },
  { id: 'America/Chicago', label: 'Chicago (CT)' },
  { id: 'America/New_York', label: 'New York (ET)' },
  { id: 'America/Denver', label: 'Denver (MT)' },
  { id: 'America/Los_Angeles', label: 'Los Angeles (PT)' },
  { id: 'UTC', label: 'UTC' },
  { id: 'Europe/London', label: 'London' },
  { id: 'Europe/Berlin', label: 'Berlin / Paris' },
  { id: 'Asia/Singapore', label: 'Singapore' },
  { id: 'Asia/Tokyo', label: 'Tokyo' },
]
let _tz = 'local'
try { _tz = localStorage.getItem(TZ_KEY) || 'local' } catch { /* noop */ }
const _subs = new Set()
export const getDisplayTz = () => _tz
export function setDisplayTz(tz) {
  _tz = tz || 'local'
  try { localStorage.setItem(TZ_KEY, _tz) } catch { /* noop */ }
  _fmtCache.clear()
  _subs.forEach(fn => { try { fn(_tz) } catch { /* noop */ } })
}
export function onDisplayTzChange(fn) { _subs.add(fn); return () => _subs.delete(fn) }

const _fmtCache = new Map()
// Intl formatter in the display zone (cached per zone + options).
export function tzFormat(opts, locale = []) {
  const k = _tz + '|' + JSON.stringify(opts) + '|' + String(locale)
  let f = _fmtCache.get(k)
  if (!f) {
    const o = _tz === 'local' ? opts : { ...opts, timeZone: _tz }
    try { f = new Intl.DateTimeFormat(locale, o) } catch { f = new Intl.DateTimeFormat(locale, opts) }
    _fmtCache.set(k, f)
  }
  return f
}
// Wall-clock fields of `ms` in the display zone.
export function tzParts(ms) {
  const p = {}
  for (const x of tzFormat({ year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23' }, 'en-US').formatToParts(new Date(ms))) p[x.type] = x.value
  return { mon: +p.month - 1, day: +p.day, h: (+p.hour) % 24, m: +p.minute, s: +p.second }
}